import { ReactNode } from "react";
import { Button, Card, CardDescription, CardTitle, Spinner, cn } from "@playerelite/ui";

export type CardStatus = "loading" | "error" | "empty" | "ready";

/** The status of a card that needs every one of `queries`, as React Query reports them. */
export function cardStatus(
  queries: { isPending: boolean; isError: boolean; data?: unknown }[],
  isEmpty: boolean
): CardStatus {
  if (queries.some((q) => q.isError && q.data === undefined)) return "error";
  if (queries.some((q) => q.isPending)) return "loading";
  return isEmpty ? "empty" : "ready";
}

type Props = {
  title: string;
  description: string;
  /** A headline figure for the period, shown at the top right. */
  summary?: ReactNode;
  status: CardStatus;
  onRetry: () => void;
  /** Keeps the card the same height while it loads, errors or is empty. */
  bodyHeight: number;
  className?: string;
  children: ReactNode;
};

const ChartCard = ({
  title,
  description,
  summary,
  status,
  onRetry,
  bodyHeight,
  className,
  children,
}: Props) => (
  <Card padding="lg" className={cn("flex min-w-0 flex-col gap-4", className)}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <CardTitle as="h2">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
      {summary && status === "ready" ? (
        <span className="whitespace-nowrap font-mono text-lg font-semibold tabular-nums text-fg">
          {summary}
        </span>
      ) : null}
    </div>
    <div style={{ minHeight: bodyHeight }} className="flex min-w-0 flex-col">
      {status === "loading" ? (
        <div className="flex flex-1 items-center justify-center text-fg-muted">
          <Spinner size="md" label={`Loading ${title.toLowerCase()}`} />
        </div>
      ) : status === "error" ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-sm text-fg-muted">
          <span>Couldn't load this chart.</span>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : status === "empty" ? (
        <div className="flex flex-1 items-center justify-center text-sm text-fg-muted">
          No data for this period.
        </div>
      ) : (
        children
      )}
    </div>
  </Card>
);

export default ChartCard;
